import api from '../api/api';

// Cache translation database to avoid loading it multiple times
let translationDbCache = null;

/**
 * Get translation for a specific verse
 * @param {string|number} surahId - Surah ID
 * @param {string|number} ayahNumber - Verse number
 * @returns {string|null} Translation text or null if not found
 */
const getTranslation = (surahId, ayahNumber) => {
  try {
    // Load translation database only once and cache it
    if (!translationDbCache) {
      translationDbCache = require('../assets/db/translation.json');
    }
    
    const key = `${surahId}:${ayahNumber}`;
    return translationDbCache[key]?.t || null;
  } catch (error) {
    console.error('Error loading translation:', error);
    return null;
  }
};

// Get a list of all surahs from the Quran
const getAllSurahs = () => {
  // This is a static list of surahs that won't change
  return [
    { id: '1', name: 'Al-Fatiha', nameArabic: 'الفاتحة', verseCount: 7 },
    { id: '2', name: 'Al-Baqarah', nameArabic: 'البقرة', verseCount: 286 },
    { id: '3', name: 'Al-Imran', nameArabic: 'آل عمران', verseCount: 200 },
    { id: '4', name: 'An-Nisa', nameArabic: 'النساء', verseCount: 176 },
    { id: '5', name: 'Al-Ma\'idah', nameArabic: 'المائدة', verseCount: 120 },
    { id: '6', name: 'Al-An\'am', nameArabic: 'الأنعام', verseCount: 165 },
    { id: '7', name: 'Al-A\'raf', nameArabic: 'الأعراف', verseCount: 206 },
    { id: '8', name: 'Al-Anfal', nameArabic: 'الأنفال', verseCount: 75 },
    { id: '9', name: 'At-Tawbah', nameArabic: 'التوبة', verseCount: 129 },
    { id: '10', name: 'Yunus', nameArabic: 'يونس', verseCount: 109 },
    { id: '11', name: 'Hud', nameArabic: 'هود', verseCount: 123 },
    { id: '12', name: 'Yusuf', nameArabic: 'يوسف', verseCount: 111 },
    { id: '13', name: 'Ar-Ra\'d', nameArabic: 'الرعد', verseCount: 43 },
    { id: '14', name: 'Ibrahim', nameArabic: 'إبراهيم', verseCount: 52 },
    { id: '15', name: 'Al-Hijr', nameArabic: 'الحجر', verseCount: 99 },
    { id: '16', name: 'An-Nahl', nameArabic: 'النحل', verseCount: 128 },
    { id: '17', name: 'Al-Isra', nameArabic: 'الإسراء', verseCount: 111 },
    { id: '18', name: 'Al-Kahf', nameArabic: 'الكهف', verseCount: 110 },
    { id: '19', name: 'Maryam', nameArabic: 'مريم', verseCount: 98 },
    { id: '20', name: 'Ta-Ha', nameArabic: 'طه', verseCount: 135 },
    { id: '21', name: 'Al-Anbiya', nameArabic: 'الأنبياء', verseCount: 112 },
    { id: '22', name: 'Al-Hajj', nameArabic: 'الحج', verseCount: 78 },
    { id: '23', name: 'Al-Mu\'minun', nameArabic: 'المؤمنون', verseCount: 118 },
    { id: '24', name: 'An-Nur', nameArabic: 'النور', verseCount: 64 },
    { id: '25', name: 'Al-Furqan', nameArabic: 'الفرقان', verseCount: 77 },
    { id: '26', name: 'Ash-Shu\'ara', nameArabic: 'الشعراء', verseCount: 227 },
    { id: '27', name: 'An-Naml', nameArabic: 'النمل', verseCount: 93 },
    { id: '28', name: 'Al-Qasas', nameArabic: 'القصص', verseCount: 88 },
    { id: '29', name: 'Al-Ankabut', nameArabic: 'العنكبوت', verseCount: 69 },
    { id: '30', name: 'Ar-Rum', nameArabic: 'الروم', verseCount: 60 },
    { id: '31', name: 'Luqman', nameArabic: 'لقمان', verseCount: 34 },
    { id: '32', name: 'As-Sajdah', nameArabic: 'السجدة', verseCount: 30 },
    { id: '33', name: 'Al-Ahzab', nameArabic: 'الأحزاب', verseCount: 73 },
    { id: '34', name: 'Saba', nameArabic: 'سبأ', verseCount: 54 },
    { id: '35', name: 'Fatir', nameArabic: 'فاطر', verseCount: 45 },
    { id: '36', name: 'Ya-Sin', nameArabic: 'يس', verseCount: 83 },
    { id: '37', name: 'As-Saffat', nameArabic: 'الصافات', verseCount: 182 },
    { id: '38', name: 'Sad', nameArabic: 'ص', verseCount: 88 },
    { id: '39', name: 'Az-Zumar', nameArabic: 'الزمر', verseCount: 75 },
    { id: '40', name: 'Ghafir', nameArabic: 'غافر', verseCount: 85 },
    { id: '41', name: 'Fussilat', nameArabic: 'فصلت', verseCount: 54 },
    { id: '42', name: 'Ash-Shura', nameArabic: 'الشورى', verseCount: 53 },
    { id: '43', name: 'Az-Zukhruf', nameArabic: 'الزخرف', verseCount: 89 },
    { id: '44', name: 'Ad-Dukhan', nameArabic: 'الدخان', verseCount: 59 },
    { id: '45', name: 'Al-Jathiya', nameArabic: 'الجاثية', verseCount: 37 },
    { id: '46', name: 'Al-Ahqaf', nameArabic: 'الأحقاف', verseCount: 35 },
    { id: '47', name: 'Muhammad', nameArabic: 'محمد', verseCount: 38 },
    { id: '48', name: 'Al-Fath', nameArabic: 'الفتح', verseCount: 29 },
    { id: '49', name: 'Al-Hujurat', nameArabic: 'الحجرات', verseCount: 18 },
    { id: '50', name: 'Qaf', nameArabic: 'ق', verseCount: 45 },
    { id: '51', name: 'Adh-Dhariyat', nameArabic: 'الذاريات', verseCount: 60 },
    { id: '52', name: 'At-Tur', nameArabic: 'الطور', verseCount: 49 },
    { id: '53', name: 'An-Najm', nameArabic: 'النجم', verseCount: 62 },
    { id: '54', name: 'Al-Qamar', nameArabic: 'القمر', verseCount: 55 },
    { id: '55', name: 'Ar-Rahman', nameArabic: 'الرحمن', verseCount: 78 },
    { id: '56', name: 'Al-Waqi\'ah', nameArabic: 'الواقعة', verseCount: 96 },
    { id: '57', name: 'Al-Hadid', nameArabic: 'الحديد', verseCount: 29 },
    { id: '58', name: 'Al-Mujadila', nameArabic: 'المجادلة', verseCount: 22 },
    { id: '59', name: 'Al-Hashr', nameArabic: 'الحشر', verseCount: 24 },
    { id: '60', name: 'Al-Mumtahanah', nameArabic: 'الممتحنة', verseCount: 13 },
    { id: '61', name: 'As-Saff', nameArabic: 'الصف', verseCount: 14 },
    { id: '62', name: 'Al-Jumu\'ah', nameArabic: 'الجمعة', verseCount: 11 },
    { id: '63', name: 'Al-Munafiqun', nameArabic: 'المنافقون', verseCount: 11 },
    { id: '64', name: 'At-Taghabun', nameArabic: 'التغابن', verseCount: 18 },
    { id: '65', name: 'At-Talaq', nameArabic: 'الطلاق', verseCount: 12 },
    { id: '66', name: 'At-Tahrim', nameArabic: 'التحريم', verseCount: 12 },
    { id: '67', name: 'Al-Mulk', nameArabic: 'الملك', verseCount: 30 },
    { id: '68', name: 'Al-Qalam', nameArabic: 'القلم', verseCount: 52 },
    { id: '69', name: 'Al-Haqqah', nameArabic: 'الحاقة', verseCount: 52 },
    { id: '70', name: 'Al-Ma\'arij', nameArabic: 'المعارج', verseCount: 44 },
    { id: '71', name: 'Nuh', nameArabic: 'نوح', verseCount: 28 },
    { id: '72', name: 'Al-Jinn', nameArabic: 'الجن', verseCount: 28 },
    { id: '73', name: 'Al-Muzzammil', nameArabic: 'المزمل', verseCount: 20 },
    { id: '74', name: 'Al-Muddathir', nameArabic: 'المدثر', verseCount: 56 },
    { id: '75', name: 'Al-Qiyamah', nameArabic: 'القيامة', verseCount: 40 },
    { id: '76', name: 'Al-Insan', nameArabic: 'الإنسان', verseCount: 31 },
    { id: '77', name: 'Al-Mursalat', nameArabic: 'المرسلات', verseCount: 50 },
    { id: '78', name: 'An-Naba', nameArabic: 'النبأ', verseCount: 40 },
    { id: '79', name: 'An-Nazi\'at', nameArabic: 'النازعات', verseCount: 46 },
    { id: '80', name: 'Abasa', nameArabic: 'عبس', verseCount: 42 },
    { id: '81', name: 'At-Takwir', nameArabic: 'التكوير', verseCount: 29 },
    { id: '82', name: 'Al-Infitar', nameArabic: 'الانفطار', verseCount: 19 },
    { id: '83', name: 'Al-Mutaffifin', nameArabic: 'المطففين', verseCount: 36 },
    { id: '84', name: 'Al-Inshiqaq', nameArabic: 'الانشقاق', verseCount: 25 },
    { id: '85', name: 'Al-Buruj', nameArabic: 'البروج', verseCount: 22 },
    { id: '86', name: 'At-Tariq', nameArabic: 'الطارق', verseCount: 17 },
    { id: '87', name: 'Al-A\'la', nameArabic: 'الأعلى', verseCount: 19 },
    { id: '88', name: 'Al-Ghashiyah', nameArabic: 'الغاشية', verseCount: 26 },
    { id: '89', name: 'Al-Fajr', nameArabic: 'الفجر', verseCount: 30 },
    { id: '90', name: 'Al-Balad', nameArabic: 'البلد', verseCount: 20 },
    { id: '91', name: 'Ash-Shams', nameArabic: 'الشمس', verseCount: 15 },
    { id: '92', name: 'Al-Lail', nameArabic: 'الليل', verseCount: 21 },
    { id: '93', name: 'Ad-Duha', nameArabic: 'الضحى', verseCount: 11 },
    { id: '94', name: 'Ash-Sharh', nameArabic: 'الشرح', verseCount: 8 },
    { id: '95', name: 'At-Tin', nameArabic: 'التين', verseCount: 8 },
    { id: '96', name: 'Al-Alaq', nameArabic: 'العلق', verseCount: 19 },
    { id: '97', name: 'Al-Qadr', nameArabic: 'القدر', verseCount: 5 },
    { id: '98', name: 'Al-Bayyinah', nameArabic: 'البينة', verseCount: 8 },
    { id: '99', name: 'Az-Zalzalah', nameArabic: 'الزلزلة', verseCount: 8 },
    { id: '100', name: 'Al-Adiyat', nameArabic: 'العاديات', verseCount: 11 },
    { id: '101', name: 'Al-Qari\'ah', nameArabic: 'القارعة', verseCount: 11 },
    { id: '102', name: 'At-Takathur', nameArabic: 'التكاثر', verseCount: 8 },
    { id: '103', name: 'Al-Asr', nameArabic: 'العصر', verseCount: 3 },
    { id: '104', name: 'Al-Humazah', nameArabic: 'الهمزة', verseCount: 9 },
    { id: '105', name: 'Al-Fil', nameArabic: 'الفيل', verseCount: 5 },
    { id: '106', name: 'Quraish', nameArabic: 'قريش', verseCount: 4 },
    { id: '107', name: 'Al-Ma\'un', nameArabic: 'الماعون', verseCount: 7 },
    { id: '108', name: 'Al-Kawthar', nameArabic: 'الكوثر', verseCount: 3 },
    { id: '109', name: 'Al-Kafirun', nameArabic: 'الكافرون', verseCount: 6 },
    { id: '110', name: 'An-Nasr', nameArabic: 'النصر', verseCount: 3 },
    { id: '111', name: 'Al-Masad', nameArabic: 'المسد', verseCount: 5 },
    { id: '112', name: 'Al-Ikhlas', nameArabic: 'الإخلاص', verseCount: 4 },
    { id: '113', name: 'Al-Falaq', nameArabic: 'الفلق', verseCount: 5 },
    { id: '114', name: 'An-Nas', nameArabic: 'الناس', verseCount: 6 }
  ];
};

/**
 * Get verse count for a specific surah
 * @param {string} surahId - Surah ID
 * @returns {number} Number of verses in the surah
 */
const getVerseCount = (surahId) => {
  const surah = getAllSurahs().find(s => s.id === surahId);
  return surah ? surah.verseCount : 0;
};

/**
 * Fetch verses from the Quran API
 * @param {string} surahId - Surah ID
 * @param {string|number} fromVerse - Starting verse number
 * @param {string|number} toVerse - Ending verse number
 * @returns {Promise<Array>} Array of verses
 */
const fetchVerses = async (surahId, fromVerse, toVerse) => {
  try {
    // Convert parameters to numbers
    const surahNum = parseInt(surahId);
    const fromNum = parseInt(fromVerse);
    const toNum = parseInt(toVerse);
    
    // Use the AlQuran Cloud API to fetch just Arabic verses
    const response = await fetch(
      `https://api.alquran.cloud/v1/surah/${surahNum}/ar.alafasy`
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch verses');
    }
    
    const data = await response.json();
    
    if (data.code === 200 && data.data && data.data.ayahs) {
      // Convert from 1-based indexing to 0-based indexing
      const startIndex = fromNum - 1;
      const endIndex = toNum;
      
      // Get only the verses in the specified range and add translations
      const verses = data.data.ayahs.slice(startIndex, endIndex);
      const versesWithTranslation = verses.map(verse => ({
        ...verse,
        translation: getTranslation(surahNum, verse.numberInSurah)
      }));
      
      return versesWithTranslation;
    } else {
      throw new Error('Invalid API response format');
    }
  } catch (error) {
    console.error('Error fetching verses:', error);
    return getPlaceholderVerses(surahId, fromVerse, toVerse);
  }
};

/**
 * Get placeholder verses when API fails
 * @param {string} surahId - Surah ID
 * @param {string|number} fromVerse - Starting verse number
 * @param {string|number} toVerse - Ending verse number
 * @returns {Array} Array of placeholder verses
 */
const getPlaceholderVerses = (surahId, fromVerse, toVerse) => {
  const from = parseInt(fromVerse);
  const to = parseInt(toVerse);
  const surah = getAllSurahs().find(s => s.id === surahId);
  
  const result = [];
  
  for (let i = from; i <= to; i++) {
    result.push({
      number: i,
      numberInSurah: i,
      surah: {
        number: parseInt(surahId),
        name: surah ? surah.name : 'Unknown Surah',
        englishName: surah ? surah.name : 'Unknown Surah',
        englishNameTranslation: '',
        numberOfAyahs: surah ? surah.verseCount : 0
      },
      text: `(Verse ${i} of ${surah ? surah.name : 'Unknown Surah'})`,
      translation: getTranslation(surahId, i),
      juz: 0,
      page: 0
    });
  }
  
  return result;
};

// Export all functions
const QuranService = {
  getAllSurahs,
  getVerseCount,
  fetchVerses,
  getPlaceholderVerses
};

export default QuranService;